import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TEMPERATURES } from "@/lib/crm";
import { fmtDateTime } from "@/lib/crm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, RefreshCw, Loader2, Database, AlertCircle, CheckCircle2, Globe, Trash2, ShieldAlert, Pencil, MessageCircle, Power } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import MigrationWizard from "@/components/crm/MigrationWizard";
import SyncHealth from "@/components/crm/SyncHealth";

export default function Settings() {
  return (
    <div className="space-y-4 animate-fade-up">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Impostazioni</h1>
        <p className="text-sm text-muted-foreground">Configurazione generale del CRM.</p>
      </div>
      <SyncHealth />
      <Tabs defaultValue="sources">
        <TabsList>
          <TabsTrigger value="sources" data-testid="tab-sources">Sorgenti Lead</TabsTrigger>
          <TabsTrigger value="sync" data-testid="tab-sync">Sincronizzazione</TabsTrigger>
          <TabsTrigger value="statuses" data-testid="tab-statuses">Stati</TabsTrigger>
          <TabsTrigger value="temperatures" data-testid="tab-temperatures">Temperature</TabsTrigger>
          <TabsTrigger value="interests" data-testid="tab-interests">Interessi</TabsTrigger>
          <TabsTrigger value="wa-templates" data-testid="tab-wa-templates">Template WhatsApp</TabsTrigger>
          <TabsTrigger value="lost" data-testid="tab-lost">Motivi perdita</TabsTrigger>
          <TabsTrigger value="migration" data-testid="tab-migration">Migrazione</TabsTrigger>
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">Manutenzione</TabsTrigger>
        </TabsList>
        <TabsContent value="sources" className="mt-4"><Sources /></TabsContent>
        <TabsContent value="sync" className="mt-4"><SyncPanel /></TabsContent>
        <TabsContent value="statuses" className="mt-4"><ConfigList endpoint="statuses" field="name" title="Stati commerciali" /></TabsContent>
        <TabsContent value="temperatures" className="mt-4"><Temperatures /></TabsContent>
        <TabsContent value="interests" className="mt-4"><ConfigList endpoint="interests" field="name" title="Interessi" /></TabsContent>
        <TabsContent value="wa-templates" className="mt-4"><WhatsAppTemplates /></TabsContent>
        <TabsContent value="lost" className="mt-4"><ConfigList endpoint="lost-reasons" field="name" title="Motivi di perdita" /></TabsContent>
        <TabsContent value="migration" className="mt-4"><MigrationWizard /></TabsContent>
        <TabsContent value="maintenance" className="mt-4"><Maintenance /></TabsContent>
      </Tabs>
    </div>
  );
}

function Sources() {
  const [sources, setSources] = useState(null);
  const [syncing, setSyncing] = useState(null);
  const [dialog, setDialog] = useState(null); // null | {} (new) | source (edit)
  const load = () => api.get("/sources").then((r) => setSources(r.data));
  useEffect(() => { load(); }, []);

  const sync = async (s) => {
    setSyncing(s.id);
    try {
      const { data } = await api.post(`/sources/${s.id}/sync`);
      toast.success(data.message || "Sincronizzato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore sincronizzazione");
    }
    setSyncing(null);
    load();
  };
  const toggleActive = async (s) => {
    await api.put(`/sources/${s.id}`, { active: !s.active });
    toast.success(s.active ? "Sorgente disattivata" : "Sorgente attivata");
    load();
  };
  const remove = async (s) => {
    try {
      await api.delete(`/sources/${s.id}`);
      toast.success("Sorgente eliminata");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossibile eliminare");
    }
  };

  if (!sources) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm flex items-start gap-2 flex-1 min-w-[260px]">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
          <div>Ogni sorgente ha <b>fonte, campagna, argomento e mapping colonne propri</b> e può essere attivata/disattivata in modo indipendente. L'importazione reale da Google Sheets richiederà un <b>Service Account Google</b> (JSON).</div>
        </div>
        <Button className="gap-1" data-testid="add-source-btn" onClick={() => setDialog({})}><Plus className="w-4 h-4" />Aggiungi sorgente</Button>
      </div>
      {sources.length === 0 && <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">Nessuna sorgente configurata. Aggiungi la prima sorgente per iniziare.</div>}
      {sources.map((s) => (
        <div key={s.id} data-testid={`source-${s.id}`} className="rounded-lg border bg-card p-4">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center">
                {s.type === "google_sheets" ? <Database className="w-5 h-5" /> : <Globe className="w-5 h-5" />}
              </div>
              <div>
                <div className="font-heading font-bold flex items-center gap-2">{s.name}
                  <span className={cn("text-xs px-2 py-0.5 rounded-full", s.active ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300" : "bg-zinc-200 text-zinc-500")}>{s.active ? "ATTIVO" : "DISATTIVO"}</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  <div>Tipo: {s.type === "google_sheets" ? "Google Sheets" : s.type} · Fonte: {s.fonte || "—"}</div>
                  <div>Campagna: {s.campaign || "—"} · Argomento: {s.interest || "—"}</div>
                  <div>Ultima sync: {s.last_sync_at ? fmtDateTime(s.last_sync_at) : "Mai"} · Lead importati: {s.imported_count}</div>
                  {s.last_error && <div className="text-amber-600 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{s.last_error}</div>}
                  {!s.last_error && s.last_sync_at && <div className="text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" />Nessun errore</div>}
                </div>
                {s.config?.sheet_url && <a href={s.config.sheet_url} target="_blank" rel="noreferrer" className="text-xs text-blue-500 hover:underline mt-1 inline-block truncate max-w-md">{s.config.sheet_url}</a>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Button data-testid={`edit-source-${s.id}`} onClick={() => setDialog(s)} variant="outline" size="sm" className="gap-1"><Pencil className="w-3.5 h-3.5" />Modifica</Button>
              <Button data-testid={`toggle-source-${s.id}`} onClick={() => toggleActive(s)} variant="ghost" size="sm" className="text-xs">{s.active ? "Disattiva" : "Attiva"}</Button>
              <Button data-testid={`sync-${s.id}`} onClick={() => sync(s)} disabled={syncing === s.id} className="gap-1" variant="outline" size="sm">
                {syncing === s.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizza
              </Button>
              <Button data-testid={`delete-source-${s.id}`} onClick={() => remove(s)} variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
          {s.mapping && Object.keys(s.mapping).length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <div className="overline text-muted-foreground mb-2">Mapping colonne</div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(s.mapping).map(([col, field]) => (
                  <span key={col} className="text-xs px-2 py-1 rounded bg-muted font-mono">{col} → {field}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      ))}
      {dialog !== null && <SourceDialog source={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function WhatsAppTemplates() {
  const [items, setItems] = useState(null);
  const [dialog, setDialog] = useState(null); // null | {} (new) | template (edit)
  const load = () => api.get("/whatsapp-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (t) => {
    await api.put(`/whatsapp-templates/${t.id}`, { active: !t.active });
    load();
  };
  const remove = async (t) => {
    if (!window.confirm(`Eliminare il template "${t.name}"?`)) return;
    await api.delete(`/whatsapp-templates/${t.id}`);
    toast.success("Template eliminato");
    load();
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="wa-templates-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Template riutilizzabili per i follow-up WhatsApp. I commerciali possono usarli ma non modificarli. Variabili: [NOME] [COGNOME] [INTERESSE] [COMMERCIALE].</p>
        <Button size="sm" className="gap-1" onClick={() => setDialog({})} data-testid="wa-template-new"><Plus className="w-4 h-4" /> Nuovo template</Button>
      </div>
      {items.length === 0 && <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">Nessun template. Creane uno per velocizzare i follow-up.</div>}
      {items.map((t) => (
        <div key={t.id} data-testid={`wa-template-${t.id}`} className={cn("rounded-lg border bg-card p-4", !t.active && "opacity-60")}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <MessageCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="font-heading font-bold">{t.name}</span>
                {t.interest && <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted border">{t.interest}</span>}
                <span className={cn("text-[11px] px-2 py-0.5 rounded-full border", t.active ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" : "bg-muted text-muted-foreground")}>{t.active ? "Attivo" : "Non attivo"}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 whitespace-pre-wrap">{t.text}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="icon" variant="ghost" onClick={() => toggle(t)} data-testid={`wa-template-toggle-${t.id}`} title={t.active ? "Disattiva" : "Attiva"}><Power className={cn("w-4 h-4", t.active ? "text-emerald-500" : "text-muted-foreground")} /></Button>
              <Button size="icon" variant="ghost" onClick={() => setDialog(t)} data-testid={`wa-template-edit-${t.id}`}><Pencil className="w-4 h-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => remove(t)} data-testid={`wa-template-delete-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          </div>
        </div>
      ))}
      {dialog && <WaTemplateDialog template={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

function WaTemplateDialog({ template, onClose, onSaved }) {
  const isEdit = !!template.id;
  const [form, setForm] = useState({ name: template.name || "", text: template.text || "", interest: template.interest || "", active: template.active !== false });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast.error("Nome e testo obbligatori"); return; }
    setSaving(true);
    try {
      const payload = { name: form.name, text: form.text, interest: form.interest || null, active: form.active };
      if (isEdit) await api.put(`/whatsapp-templates/${template.id}`, payload);
      else await api.post("/whatsapp-templates", payload);
      toast.success(isEdit ? "Template aggiornato" : "Template creato");
      onSaved();
    } catch (e) { toast.error("Errore nel salvataggio"); }
    setSaving(false);
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="wa-template-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Modifica template" : "Nuovo template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Nome *</Label><Input data-testid="wa-template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Interesse / corso associato (opzionale)</Label><Input data-testid="wa-template-interest" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} placeholder="es. Patentino Agente Immobiliare" /></div>
          <div className="space-y-1">
            <Label className="text-xs">Testo *</Label>
            <Textarea data-testid="wa-template-text" rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Ciao [NOME], sono [COMMERCIALE] di ANSI Formazione in merito a [INTERESSE]…" />
            <div className="flex flex-wrap gap-1 pt-1">{["[NOME]", "[COGNOME]", "[INTERESSE]", "[COMMERCIALE]"].map((v) => (
              <button key={v} type="button" onClick={() => setForm((f) => ({ ...f, text: f.text + v }))} className="text-[11px] px-2 py-0.5 rounded-full border bg-muted hover:bg-accent">{v}</button>
            ))}</div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="wa-template-active" />
            Attivo
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="wa-template-save">{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SyncPanel() {
  const [sources, setSources] = useState(null);
  const [confirmSrc, setConfirmSrc] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/sources").then((r) => setSources(r.data.filter((s) => s.type === "google_sheets")));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const anySync = (sources || []).some((s) => s.syncing);
    if (!anySync) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [sources]);

  const runSync = async () => {
    if (!confirmSrc) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/sources/${confirmSrc.id}/sync`);
      toast.success(data.message || "Sincronizzazione avviata");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore sincronizzazione");
    }
    setBusy(false);
    setConfirmSrc(null);
    setTimeout(load, 1500);
  };

  if (!sources) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3" data-testid="sync-panel">
      <div className="rounded-md border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm flex items-start gap-2">
        <RefreshCw className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
        <div>La sincronizzazione è <b>solo Google Sheets → CRM</b> (mai il contrario) e <b>non modifica mai il foglio</b>. Importa nuove righe e aggiorna i lead esistenti secondo le regole di identità già approvate, <b>senza creare duplicati</b>. Avviene automaticamente ogni ora; qui puoi forzarla manualmente.</div>
      </div>
      {sources.length === 0 && <div className="rounded-lg border border-dashed bg-card p-8 text-center text-sm text-muted-foreground">Nessuna sorgente Google Sheets configurata.</div>}
      {sources.map((s) => {
        const c = s.last_counts || {};
        const esito = s.syncing ? "in_corso" : (s.last_error ? "errore" : (s.last_sync_at ? "ok" : "mai"));
        return (
          <div key={s.id} data-testid={`sync-source-${s.id}`} className="rounded-lg border bg-card p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-primary/10 text-primary grid place-items-center"><Database className="w-5 h-5" /></div>
                <div>
                  <div className="font-heading font-bold">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Ultima sincronizzazione: <b className="text-foreground">{s.last_sync_at ? fmtDateTime(s.last_sync_at) : "Mai"}</b>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5 text-xs" data-testid={`sync-status-${s.id}`}>
                    {esito === "in_corso" && <span className="inline-flex items-center gap-1 text-blue-600"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sincronizzazione in corso…</span>}
                    {esito === "ok" && <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="w-3.5 h-3.5" /> Completata senza errori</span>}
                    {esito === "errore" && <span className="inline-flex items-center gap-1 text-destructive"><AlertCircle className="w-3.5 h-3.5" /> {s.last_error}</span>}
                    {esito === "mai" && <span className="inline-flex items-center gap-1 text-muted-foreground"><AlertCircle className="w-3.5 h-3.5" /> Mai sincronizzata</span>}
                  </div>
                </div>
              </div>
              <Button data-testid={`sync-now-${s.id}`} onClick={() => setConfirmSrc(s)} disabled={s.syncing} className="gap-1.5" size="sm">
                {s.syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizza ora
              </Button>
            </div>
            <div className="mt-3 pt-3 border-t grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`sync-counts-${s.id}`}>
              {[["Nuovi lead", c.created], ["Aggiornati", c.updated], ["Invariati", c.unchanged], ["Errori", c.errors]].map(([label, val]) => (
                <div key={label} className={cn("rounded-md border bg-background px-3 py-2", label === "Errori" && val > 0 && "border-destructive/40")}>
                  <div className={cn("font-heading text-lg font-black tabular-nums", label === "Errori" && val > 0 && "text-destructive")}>{val ?? "—"}</div>
                  <div className="text-[11px] text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <Dialog open={!!confirmSrc} onOpenChange={(o) => { if (!o) setConfirmSrc(null); }}>
        <DialogContent className="max-w-md" data-testid="sync-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><RefreshCw className="w-5 h-5 text-blue-500" /> Conferma sincronizzazione</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <p>Avviare ora la sincronizzazione <b>Google Sheets → CRM</b> della sorgente <b>{confirmSrc?.name}</b>?</p>
            <p className="text-muted-foreground">Verranno importate le nuove righe e aggiornati i lead esistenti secondo le regole già approvate. Il foglio non verrà mai modificato e non verranno creati duplicati.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSrc(null)}>Annulla</Button>
            <Button onClick={runSync} disabled={busy} data-testid="sync-confirm-btn" className="gap-1.5">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Sincronizza ora</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceDialog({ source, onClose, onSaved }) {
  const isEdit = !!source?.id;
  const [form, setForm] = useState({
    name: source?.name || "", type: source?.type || "google_sheets",
    fonte: source?.fonte || "Meta Ads", campaign: source?.campaign || "",
    interest: source?.interest || "", active: source?.active ?? true,
  });
  const [interests, setInterests] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/interests").then((r) => setInterests(r.data.map((i) => i.name))); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Il nome è obbligatorio"); return; }
    setBusy(true);
    try {
      const payload = { name: form.name.trim(), type: form.type, fonte: form.fonte || null,
                        campaign: form.campaign || null, interest: form.interest || null, active: form.active };
      if (isEdit) await api.put(`/sources/${source.id}`, payload);
      else await api.post("/sources", payload);
      toast.success(isEdit ? "Sorgente aggiornata" : "Sorgente creata");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="source-dialog">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Modifica sorgente" : "Nuova sorgente"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Nome sorgente *</Label>
            <Input data-testid="source-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Google Sheet 60 CFU" /></div>
          <div className="space-y-1"><Label className="text-xs">Tipo</Label>
            <select data-testid="source-type" className="w-full h-9 rounded-md border bg-background px-3 text-sm" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="google_sheets">Google Sheets</option>
              <option value="csv">File CSV</option>
              <option value="manual">Manuale / Altro</option>
            </select></div>
          <div className="space-y-1"><Label className="text-xs">Fonte</Label>
            <Input data-testid="source-fonte" value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })} placeholder="Es. Meta Ads, Google Ads, Sito" /></div>
          <div className="space-y-1"><Label className="text-xs">Campagna (default)</Label>
            <Input data-testid="source-campaign" value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} placeholder="Es. 60 CFU — Lead Gen Maggio" /></div>
          <div className="space-y-1"><Label className="text-xs">Argomento / Interesse / Corso (default)</Label>
            <Input data-testid="source-interest" list="interests-list" value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} placeholder="Es. 60 CFU, Master L-2" />
            <datalist id="interests-list">{interests.map((i) => <option key={i} value={i} />)}</datalist></div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" data-testid="source-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Sorgente attiva
          </label>
          <p className="text-xs text-muted-foreground">Il mapping delle colonne si configura durante l'importazione (scheda Migrazione), selezionando questa sorgente.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={busy} data-testid="source-save" className="gap-1">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{isEdit ? "Salva" : "Crea sorgente"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConfigList({ endpoint, field, title }) {
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const errMsg = (e) => {
    const d = e.response?.data?.detail;
    return Array.isArray(d) ? (d[0]?.msg || "Errore").replace(/^Value error, /, "") : d || "Errore";
  };
  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/${endpoint}`, { name });
      toast.success("Aggiunto"); setName(""); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = async (item) => {
    try { await api.put(`/${endpoint}/${item.id}`, { active: !item.active }); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="font-heading font-bold">{title}</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" className="gap-1" data-testid={`add-${endpoint}`}><Plus className="w-4 h-4" />Aggiungi</Button></DialogTrigger>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="font-heading">Aggiungi {title.toLowerCase()}</DialogTitle></DialogHeader>
            <div className="space-y-1"><Label className="text-xs">Nome</Label><Input data-testid={`input-${endpoint}`} value={name} onChange={(e) => setName(e.target.value)} /></div>
            <DialogFooter><Button onClick={add} data-testid={`save-${endpoint}`}>Aggiungi</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="rounded-lg border bg-card divide-y">
        {items.map((item) => (
          <div key={item.id} className="flex items-center justify-between px-4 py-2.5" data-testid={`config-${item.id}`}>
            <span className={cn("text-sm", !item.active && "line-through text-muted-foreground")}>{item[field]}</span>
            <Button variant="ghost" size="sm" onClick={() => toggle(item)} className="text-xs">{item.active ? "Disattiva" : "Attiva"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Temperatures() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/temperatures").then((r) => setItems(r.data)); }, []);
  if (!items) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  return (
    <div className="space-y-3">
      <h3 className="font-heading font-bold">Temperature</h3>
      <div className="rounded-lg border bg-card divide-y">
        {items.map((t) => (
          <div key={t.id} className="flex items-center gap-3 px-4 py-3">
            <span className="w-6 h-6 rounded-full" style={{ background: t.color }} />
            <span className="font-medium">{t.label}</span>
            <span className="ml-auto font-mono text-xs text-muted-foreground">{t.color}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const PURGE_LABELS = {
  leads: "Lead", appointments: "Appuntamenti", tasks: "Attività / Follow-up",
  timeline_events: "Note e timeline", lead_acquisitions: "Acquisizioni",
  notifications: "Notifiche", sync_logs: "Log sincronizzazioni",
  migration_jobs: "Job migrazione", migration_records: "Record migrazione",
  utenti_demo: "Utenti demo",
};

function Maintenance() {
  const [summary, setSummary] = useState(null);
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const load = () => api.get("/admin/demo-data/summary").then((r) => setSummary(r.data)).catch(() => setSummary({}));
  useEffect(() => { load(); }, []);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/demo-data/purge?confirm=true");
      setResult(data);
      toast.success(`Eliminati ${data.total_deleted} record demo/test`);
      setOpen(false); setConfirmText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante la pulizia");
    }
    setBusy(false);
  };

  const total = summary?.totale ?? 0;

  return (
    <div className="space-y-4" data-testid="maintenance-panel">
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-md bg-destructive/10 text-destructive grid place-items-center shrink-0"><ShieldAlert className="w-5 h-5" /></div>
          <div className="flex-1">
            <h3 className="font-heading font-bold">Svuota dati demo / test</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Elimina in modo permanente tutti i dati transazionali (lead, appuntamenti, attività, follow-up, note/timeline, trattative, valori economici, acquisizioni, notifiche) e gli utenti demo.
              <b className="text-foreground"> Vengono preservati</b>: il tuo account admin, gli utenti reali, stati, temperature, interessi, motivi di perdita, automazioni, sorgenti e tutte le configurazioni. Nessun dato demo verrà ricreato.
            </p>
          </div>
        </div>

        {summary === null ? (
          <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-testid="maintenance-summary">
            {Object.entries(PURGE_LABELS).map(([k, label]) => (
              <div key={k} className="rounded-md border bg-background px-3 py-2">
                <div className="font-heading text-lg font-black tabular-nums">{summary[k] ?? 0}</div>
                <div className="text-[11px] text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); setConfirmText(""); }}>
            <DialogTrigger asChild>
              <Button variant="destructive" className="gap-1.5" data-testid="purge-demo-btn" disabled={total === 0}>
                <Trash2 className="w-4 h-4" /> Svuota dati demo / test
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-destructive" /> Conferma eliminazione</DialogTitle></DialogHeader>
              <div className="space-y-3 text-sm">
                <p>Stai per eliminare <b>{total}</b> record demo/test. L'operazione è <b>irreversibile</b> ma non tocca configurazioni, admin e utenti reali.</p>
                <div className="space-y-1">
                  <Label className="text-xs">Per confermare digita <b>ELIMINA</b></Label>
                  <Input data-testid="purge-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="ELIMINA" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
                <Button variant="destructive" onClick={purge} disabled={confirmText !== "ELIMINA" || busy} data-testid="purge-confirm-btn" className="gap-1.5">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />} Elimina definitivamente
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {total === 0 && <span className="text-sm text-emerald-600 flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Nessun dato demo/test presente. Database pulito.</span>}
        </div>
      </div>

      {result && (
        <div className="rounded-lg border bg-card p-4 text-sm" data-testid="maintenance-result">
          <div className="font-heading font-bold mb-2 flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> Pulizia completata</div>
          <p className="text-muted-foreground">Eliminati <b className="text-foreground">{result.total_deleted}</b> record. Statistiche sorgenti azzerate: {result.sorgenti_statistiche_azzerate}. Preservati: {result.preserved.utenti_reali} utenti, {result.preserved.stati} stati, {result.preserved.temperature} temperature, {result.preserved.interessi} interessi, {result.preserved.motivi_perdita} motivi perdita, {result.preserved.automazioni} automazioni, {result.preserved.sorgenti} sorgenti.</p>
        </div>
      )}
    </div>
  );
}